import { createIntl } from 'react-intl';
import { all, call, put, fork, takeLatest, select } from 'redux-saga/effects';

import { ExtrasManagementsEnum } from 'components/configuration/common/AssignmentExtras/ExtrasManagement.constants';
import { restClient } from 'config/store/configureStore';
import { SELF_SERVICE } from 'constants/ExtensionConstants';
import { getLocaleUsage } from 'constants/LanguageConstants';
import { TECHNICIAN } from 'constants/ModeConstants';
import { APPLICATION_IS_READY } from 'modules/app/actionTypes';
import { putAndWait } from 'modules/app/sagas';
import { fetchPage } from 'modules/app/sagas/resolveAll';
import { simpleRequestsLoader } from 'modules/app/utils';
import { selectDealer } from 'modules/dealers/actions';
import { getCurrency } from 'modules/groups/actions';
import * as groupActions from 'modules/groups/actions';
import { getSelectedGroup, getSelectedGroupId } from 'modules/groups/selectors';
import { getLocale, getTranslationsForCurrentLocale } from 'modules/i18n/selectors/data';
import { getLocation } from 'modules/router/selectors';
import { createNotification } from 'modules/toast-notifications/actions';
import { CURRENT_USER_DATA_SUCCESS } from 'modules/users/actionTypes';
import { getSelectedDealer } from 'selectors/dealerSelectors';

import { ucFirst } from 'utils/global';

import groupConfig from './groupConfig';
import groupManagement from './groupManagement';
import * as actionTypes from '../actionTypes';
import requestsConfig from '../requestsConfig';

const selfServiceEntities = [ExtrasManagementsEnum.EXTRAS_TYPE, ExtrasManagementsEnum.BREAKDOWN_EXTRAS_TYPE];

const entityTranslationAliases = {
  [ExtrasManagementsEnum.BREAKDOWN_EXTRAS_TYPE]: ExtrasManagementsEnum.EXTRAS_TYPE,
};

/** @param {import("../types/actions").FetchGroupsSelectAction} action */
function* fetchGroupsSelect({ payload }) {
  const { request } = payload;
  try {
    const data = yield* fetchPage(request);
    yield put({ type: actionTypes.FETCH_GROUPS_SELECT_SUCCESS, payload: data });
  } catch (error) {
    yield put({ type: actionTypes.FETCH_GROUPS_SELECT_FAILURE, payload: error, error: true });
  }
}

/** @param {import("../types/actions").FetchGroupAction} action */
function* fetchGroup({ payload }) {
  const { request, groupId } = payload;
  try {
    const { data: group } = yield call(restClient.request, request);
    const { error, payload: dataCurrency } = yield putAndWait(getCurrency(groupId));
    yield put(groupActions.fetchGroupSuccess({ ...(error ? {} : dataCurrency), ...group }));
  } catch (error) {
    yield put(groupActions.fetchGroupFailure(error));
  }
}

/** @param {import("../types/actions").SelectGroupAction} action */
function* selectGroup({ payload: { groupId, params }, meta }) {
  const selectedGroup = yield select(getSelectedGroup);
  if (groupId && (groupId !== selectedGroup?.id || !selectedGroup?.extensions || meta.forceReload)) {
    yield put(groupActions.fetchGroup(groupId, params));

    const dealer = yield select(getSelectedDealer);
    const dealerGroupId = dealer?.group?.id ?? dealer?.groupId;
    if (!meta.ignoreSelectDealer && dealer && dealerGroupId !== groupId) {
      yield put(selectDealer(''));
    }
  }
}

/** @param {import("modules/users/types/actions").FetchDataForCurrentUserSuccessAction} action */
function* loadGroupAtStartupWorker({ payload: userData }) {
  const { groupId } = userData;
  if (groupId) {
    yield put(groupActions.selectGroup(userData.groupId));
  }
}

// Creation pages carry the group they were opened for in the navigation state (BMWVID-29089).
// Runs once the application is ready — after the boot flows that derive the group from the
// user or the selected dealer — so a hard refresh restores the group instead of the derived one.
// A state carrying a dealerId is a DEALER-page context: the dealer restore already selected the
// dealer and derived its group, so the stamped groupId must not override (it would clear the dealer).
function* restoreGroupFromLocationState() {
  const { state } = yield select(getLocation);
  const selectedGroupId = yield select(getSelectedGroupId);

  if (state?.groupId && !state.dealerId && state.groupId !== selectedGroupId) {
    yield put(groupActions.selectGroup(state.groupId));
  }
}

function* getIntlProvider() {
  const locale = yield select(getLocale);
  const userLocaleVariant = getLocaleUsage(locale).i18n;
  const messages = yield select(getTranslationsForCurrentLocale);

  return createIntl({ locale: userLocaleVariant, messages, textComponent: 'span' });
}

/** @param {import("../types/actions").AssignmentGroupToDealersSuccessAction} action */
function* onAssignmentSuccess({ payload }) {
  const { entity, dataRequest } = payload;
  const intl = yield call(getIntlProvider);

  const { modes = [], modules = [] } = dataRequest;
  const entityKey = entityTranslationAliases[entity] ?? entity;

  const message = {
    id: 'page.assignments.modal.notifications.success',
    defaultMessage: '{hasMode, select, false {} other {{modes} - }}{hasModule, select, false {} other {{modules} - } } {entity} have been assigned correctly.',
    values: {
      entity: intl.formatMessage({ id: entityKey, defaultMessage: ucFirst(entityKey) }),
      hasMode: modes.length > 0,
      hasModule: modules.length > 0,
      modes: intl.formatList(
        modes.map(mode => {
          const isSelfService = mode === TECHNICIAN && selfServiceEntities.includes(entity);

          const translationParams = isSelfService
            ? { id: 'interventionKanban.title', defaultMessage: ucFirst(SELF_SERVICE) }
            : { id: `fleetbackMode.${mode}`, defaultMessage: ucFirst(mode) };

          return intl.formatMessage(translationParams);
        }),
        { type: 'conjunction' }
      ),
      modules: intl.formatList(
        modules.map(module => intl.formatMessage({ id: module.toLowerCase(), defaultMessage: ucFirst(module) })),
        { type: 'conjunction' }
      ),
    },
  };

  yield put(createNotification({ type: 'success', message }));
}

export default function* newGroupsSagas() {
  // Load simple requests
  yield all(simpleRequestsLoader(requestsConfig));

  // Watchers
  yield takeLatest(actionTypes.FETCH_GROUPS_SELECT_REQUEST, fetchGroupsSelect);
  yield takeLatest(actionTypes.SELECT_GROUP, selectGroup);
  yield takeLatest(actionTypes.FETCH_GROUP_REQUEST, fetchGroup);
  yield takeLatest(CURRENT_USER_DATA_SUCCESS, loadGroupAtStartupWorker);
  yield takeLatest(APPLICATION_IS_READY, restoreGroupFromLocationState);
  yield takeLatest(actionTypes.ASSIGNMENT_GROUP_TO_DEALERS_SUCCESS, onAssignmentSuccess);

  // Additional group-related sagas
  yield fork(groupConfig);
  yield fork(groupManagement);
}
